import fs from 'node:fs/promises'
import as from 'ansis'
import { Command } from 'commander'
import YAML from 'yaml'
import { z } from 'zod'

import { join, normalize } from '@scalar/openapi-parser'

import { isYamlFileName } from '@/domains/document/helpers'
import { getPrefixes } from '@/domains/document/join/helpers'
import { output } from '@/helpers/std-out'
import { measureTime } from '@/helpers/time'

type Conflicts = Extract<
  Awaited<ReturnType<typeof join>>,
  { ok: false }
>['conflicts']

/**
 * Formats an array of conflict objects into human-readable error messages.
 *
 * @param conflicts - Array of conflict objects returned from the join operation.
 * @returns Array of formatted error message strings describing each conflict.
 */
const formatErrorMessage = (conflicts: Conflicts) => {
  return conflicts.map((conflict) => {
    // Handle component conflicts
    if (conflict.type === 'component') {
      return `Component '/${conflict.componentType}/${conflict.name}' has conflicts`
    }

    // Handle path conflicts
    if (conflict.type === 'path') {
      return `Path '${conflict.path} ${conflict.method}' has conflicts`
    }

    // Handle webhook conflicts (default case)
    return `Webhook '${conflict.path} ${conflict.method}' has conflicts`
  })
}

export function JoinCommand() {
  const cmd = new Command('join')

  cmd.description(
    'Merge multiple OpenAPI documents into a single unified document',
  )
  cmd.argument('<files...>', 'Paths to the OpenAPI files to merge')
  cmd.option('--output -o <file>', 'Path to save the merged output file')
  cmd.option(
    '--prefix-components-with-path-value -p <path>',
    'Dot-separated path to extract a prefix value from each input file for component names',
  )
  cmd.action(async (rawInput, rawOpt) => {
    const { error, data: input } = z.array(z.string()).safeParse(rawInput)
    if (error) {
      return output
        .error()
        .title('Invalid input')
        .message(error.issues[0].message)
        .exit('error')
    }

    const { error: optsError, data: opts } = z
      .object({
        output: z.string(),
        prefixComponentsWithPathValue: z.string().optional(),
      })
      .safeParse(rawOpt)
    if (optsError) {
      return output
        .error()
        .title('Invalid options')
        .line('')
        .line(
          optsError.issues
            .map((e) => `${e.message} property: '${e.path}'`)
            .join('\n'),
        )
        .line('')
        .exit('error')
    }

    // Read files from input
    const files = (
      await Promise.all(input.map((file) => fs.readFile(file, 'utf-8')))
    ).map((it) => normalize(it) as Record<string, unknown>)

    const prefixes = opts.prefixComponentsWithPathValue
      ? getPrefixes(files, opts.prefixComponentsWithPathValue)
      : []

    const { executionTimeMs, result } = await measureTime(() =>
      join(files, { prefixComponents: prefixes }),
    )

    if (!result.ok) {
      return output
        .error()
        .title('Failed to join OpenAPI documents')
        .line()
        .line(`Conflicts:\n${formatErrorMessage(result.conflicts).join('\n')}`)
        .line()
        .exit('error')
    }

    const isYaml = isYamlFileName(opts.output)

    // Write to the file system
    await fs.writeFile(
      opts.output,
      isYaml
        ? YAML.stringify(result.document)
        : JSON.stringify(result.document, null, 2),
      'utf8',
    )

    output
      .info()
      .message(
        `${as.green('🤝 OpenAPI Documents joined')} in ${as.white(`${as.bold(`${Math.round(executionTimeMs)}`)} ms`)}`,
      )
      .print()
  })

  return cmd
}
